import React, { useState, Fragment, useEffect } from "react";
import { Element as ScrollElement, scroller } from "react-scroll";
import { DateTime } from "luxon";
import usePreviousDistinct from "react-use/lib/usePreviousDistinct";
import {
  getLocationPlans,
  FamilyPlanOptions,
  toCurrency,
  Location,
  User,
  calculateFamilyPlanCost,
  getPlanAddonsForFamilyUser,
  putCommasIntoNumber,
  LocalProjectAddon,
  addDaySuffix,
} from "@ecologi/shared";
import { LocalProjectAddonBanner } from "../../LocalProjectAddonBanner";
import { CheckoutTotalTable } from "../../CheckoutTotalTable";
import { PlanChangeConfirmModal } from "../../PlanChangeConfirmModal";
import { Button } from "../../Button";
import { Wrapper } from "../../Wrapper";
import {
  CheckoutForm,
  CheckoutFormProps,
  CheckoutFormBaseProps,
} from "../../CheckoutForm";
import { PlanCard } from "./PlanCard";
import { PlanFamilyCard } from "./PlanFamilyCard";
import { PlanSelectButtons, planTypes } from "./PlanSelectButtons";
import { usePlanEffects } from "../hooks/use-plan-effects";
import { ContinueButton } from "./ContinueButton";
import { YoureAwesome } from "../../YoureAwesome";

import styles from "../styles/Plan.module.scss";

const createFormattedDate = (billingAt: Date) => {
  const dayWithSuffix = addDaySuffix(
    DateTime.fromJSDate(billingAt).toFormat("d")
  );
  const [month, year] = DateTime.fromJSDate(billingAt)
    .toFormat(`MMM kkkk`)
    .split(" ");

  return `${dayWithSuffix} ${month}, ${year}`;
};

const hasFamilyAddonsChanged = (
  newAddons: FamilyPlanOptions,
  oldAddons: FamilyPlanOptions
) => {
  const hasAdultChanged = newAddons.adults !== oldAddons.adults;
  const hasChildrenChanged = newAddons.children !== oldAddons.children;
  const hasPetsChanged = newAddons.pets !== oldAddons.pets;

  return hasAdultChanged || hasChildrenChanged || hasPetsChanged;
};

const emptyFamilyPlan = {
  adults: 0,
  children: 0,
  pets: 0,
};

export const getFootprintFraction = (n?: number) => {
  switch (n) {
    case 0.25:
      return "1/4";
    case 0.33:
      return "1/3";
    case 0.5:
      return "1/2";
    default:
      return n;
  }
};

type Props = {
  location: Location | null;
  user: User;
  updateSubscription: (args: {
    shortCode: string;
    addons: FamilyPlanOptions;
    localProjectAddon: LocalProjectAddon;
    numLocalTrees: number;
  }) => Promise<unknown>;
  isLoading: boolean;
  isUpdateSubscription: boolean;
  renderCheckoutForm: (props: CheckoutFormBaseProps) => JSX.Element;
  selectedPlanType: string;
  onSelectedPlanTypeChange: (planType: string) => void;
  selectedPlanId: string | null;
  onSelectedPlanIdChange: (planId: string | null) => void;
};

function CheckoutFormWithWrapper(props: CheckoutFormProps) {
  return (
    <div className={styles.planCheckoutWrapper}>
      <ScrollElement name="checkout">
        <CheckoutForm {...props} />
      </ScrollElement>
    </div>
  );
}

export const Plan = ({
  user,
  location,
  updateSubscription,
  isLoading,
  isUpdateSubscription,
  renderCheckoutForm,
  selectedPlanType,
  onSelectedPlanTypeChange,
  selectedPlanId,
  onSelectedPlanIdChange,
}: Props) => {
  const [isCheckoutVisible, setCheckoutVisibility] = useState(false);
  const [selectedFamilyPlanAddons, setSelectedFamilyPlanAddons] =
    useState<FamilyPlanOptions>(emptyFamilyPlan);
  const [numLocalTrees, setNumLocalTrees] = useState(0);
  const [isUpdateModalVisible, setUpdateModalVisibility] = useState(false);

  // Keep a track of the previous location and reset
  // the selected plan id if it changes
  const prevCountryCode = usePreviousDistinct(
    location ? location.countryCode : ""
  );
  useEffect(() => {
    setCheckoutVisibility(false);
    onSelectedPlanIdChange(null);
    setNumLocalTrees(0);
  }, [
    prevCountryCode,
    onSelectedPlanIdChange,
    setCheckoutVisibility,
    setNumLocalTrees,
  ]);

  const {
    currencySymbol,
    familyAddonsForLocation,
    familyPlanIdForLocation,
    plansForLocation,
    localProjectAddon,
  } = getLocationPlans(location);

  const selectedPlan = selectedPlanId ? plansForLocation[selectedPlanId] : null;
  const isFamilyPlan = selectedPlanType === planTypes.family;
  const isFamilyValid =
    selectedFamilyPlanAddons.adults + selectedFamilyPlanAddons.children > 0;

  usePlanEffects({
    familyPlanIdForLocation,
    selectedPlanType,
    setNumLocalTrees,
    setSelectedFamilyPlanAddons,
    setSelectedPlanId: onSelectedPlanIdChange,
    setSelectedPlanType: onSelectedPlanTypeChange,
    user,
  });

  // If a business sub lands here somehow, show them the awesome screen
  if (user && (user.isBusinessPlan || !user.subscription)) {
    return <YoureAwesome user={user} />;
  }

  let planTotal = isFamilyPlan
    ? calculateFamilyPlanCost(familyAddonsForLocation, selectedFamilyPlanAddons)
    : toCurrency(selectedPlan ? selectedPlan.price : 0);

  if (numLocalTrees > 0 && localProjectAddon) {
    planTotal = toCurrency(
      parseFloat(planTotal) +
        localProjectAddon.subscriptionPrice * numLocalTrees
    );
  }

  const currentPlan =
    user && user.subscription
      ? { ...plansForLocation }[user.subscription.planId]
      : null;

  let currentSubscriptionTotal =
    user && user.subscription
      ? user.subscription.planId.includes("family")
        ? calculateFamilyPlanCost(
            familyAddonsForLocation,
            getPlanAddonsForFamilyUser(user)
          )
        : toCurrency(currentPlan ? currentPlan.price : 0)
      : "0";

  if (user && user.getLocalProjectAddons().length > 0 && localProjectAddon) {
    currentSubscriptionTotal = toCurrency(
      parseFloat(currentSubscriptionTotal) +
        localProjectAddon.subscriptionPrice *
          user.getLocalProjectAddons()[0].quantity
    );
  }

  const isFamilyAddonsChanged =
    user && isFamilyPlan
      ? hasFamilyAddonsChanged(
          selectedFamilyPlanAddons,
          getPlanAddonsForFamilyUser(user)
        )
      : false;

  const userLocalProjectAddons = user ? user.getLocalProjectAddons() : [];
  const isLocalProjectAddonsChanged =
    userLocalProjectAddons.length > 0
      ? userLocalProjectAddons[0].quantity !== numLocalTrees
      : numLocalTrees > 0;

  const _selectedPlanId = isFamilyPlan
    ? familyPlanIdForLocation
    : selectedPlanId;

  const nextBillingDate = user?.subscription?.nextBillingAt;

  const handleSelectPlan = (planId: string) => {
    onSelectedPlanIdChange(planId);
    // If there isnt a local project, send them straight to
    // the checkout
    if (!localProjectAddon) {
      setCheckoutVisibility(true);
    }
    setTimeout(() => {
      scroller.scrollTo(localProjectAddon ? "local-projects" : "checkout", {
        duration: 1000,
        offset: localProjectAddon ? 0 : -40,
        smooth: true,
      });
    }, 300);
  };

  return (
    <Fragment>
      <Fragment>
        <Wrapper isLarge className={styles.pageWrapper}>
          <h1 className={styles.planTitle}>
            {isUpdateSubscription
              ? "Change your subscription"
              : "Select your monthly plan"}
          </h1>
          <h2 className={styles.planSubtitle}>
            {isUpdateSubscription
              ? "Select a new plan to start next month"
              : "Plant trees and fund carbon avoidance"}
          </h2>

          <PlanSelectButtons
            selectedPlanType={selectedPlanType}
            setSelectedPlanType={(planType: string) => {
              onSelectedPlanTypeChange(planType);
              onSelectedPlanIdChange(null);
              setSelectedFamilyPlanAddons(emptyFamilyPlan);
              setCheckoutVisibility(false);
            }}
            isUpdateSubscription={isUpdateSubscription}
          />

          {selectedPlanType === planTypes.personal ? (
            <section className={styles.planCards}>
              {Object.values(plansForLocation)
                .map((plan) => (
                  <PlanCard
                    currencySymbol={currencySymbol}
                    isSelected={plan.id === selectedPlanId}
                    key={plan.id}
                    plan={plan}
                    selectPlan={handleSelectPlan}
                  />
                ))
                .reverse()}
            </section>
          ) : (
            <PlanFamilyCard
              isUpdateSubscription={isUpdateSubscription}
              location={location}
              selectedFamilyPlanAddons={selectedFamilyPlanAddons}
              setSelectedFamilyPlanAddons={setSelectedFamilyPlanAddons}
            />
          )}
          <div className={styles.planCardsInfo}>
            {selectedPlanType === planTypes.personal && (
              <p>
                * Footprints are for illustrative purposes only. At Ecologi, we
                believe that funding climate solutions is vital, but it doesn't
                diminish your own carbon emissions - and should therefore be
                carried out alongside steps you take to reduce your own
                footprint. As part of your subscription, we will fund the
                prevention or avoidance of approximately 1/3 (Starter), 1
                (Essential) or 2 (Ultimate) footprints' worth of carbon
                emissions, during each year you're subscribed.
              </p>
            )}
          </div>
        </Wrapper>
        <ScrollElement name="local-projects">
          <LocalProjectAddonBanner
            {...{ numLocalTrees, setNumLocalTrees }}
            location={location}
          />
        </ScrollElement>
        {(numLocalTrees > 0 || isFamilyPlan) && !isUpdateSubscription && (
          <CheckoutTotalTable
            location={location}
            localProjectAddon={localProjectAddon}
            numLocalTrees={numLocalTrees}
            selectedPlanAddons={selectedFamilyPlanAddons}
            selectedPlanId={_selectedPlanId}
          />
        )}

        {!isUpdateSubscription && (!!localProjectAddon || isFamilyPlan) && (
          <div
            className={styles.buttonWrapper}
            data-is-local-project-available={!!localProjectAddon}
            data-is-plan-selected={!!selectedPlanId || isFamilyValid}
          >
            <div
              className={styles.buttonDisabler}
              data-is-checkout-visible={isCheckoutVisible}
            >
              <ContinueButton
                isDisabled={
                  !_selectedPlanId || (isFamilyPlan && !isFamilyValid)
                }
                isFamilyPlan={isFamilyPlan}
                onClick={() => {
                  setCheckoutVisibility(true);
                  setTimeout(() => {
                    scroller.scrollTo("checkout", {
                      duration: 1000,
                      offset: -40,
                      smooth: true,
                    });
                  }, 300);
                }}
              />
            </div>
          </div>
        )}

        <div className={styles.divider} />

        {!isUpdateSubscription && isCheckoutVisible && (
          <section
            className={styles.planCheckout}
            style={localProjectAddon ? {} : { paddingTop: "40px" }}
          >
            {renderCheckoutForm({
              selectedPlanAddons: selectedFamilyPlanAddons,
              selectedPlanId: _selectedPlanId,
              numLocalTrees,
            })}
          </section>
        )}

        {isUpdateSubscription && user.subscription && (
          <div style={{ background: "white" }}>
            <Wrapper
              style={{
                padding: !!localProjectAddon ? "0 0 50px" : "50px 0",
                fontSize: "20px",
                textAlign: "center",
              }}
            >
              <p style={{ marginBottom: "20px" }}>
                Your current subscription is for{" "}
                <strong>
                  {currencySymbol}
                  {putCommasIntoNumber(currentSubscriptionTotal)}
                </strong>{" "}
                per month.
              </p>
              {(selectedPlan || isFamilyValid) && (
                <Fragment>
                  {(selectedPlanId !== user.subscription.planId ||
                    isFamilyAddonsChanged ||
                    isLocalProjectAddonsChanged) && (
                    <Fragment>
                      <p style={{ marginBottom: "15px" }}>
                        If you change your subscription, your future charges
                        will be for{" "}
                        <strong>
                          {currencySymbol}
                          {planTotal}
                        </strong>
                        .
                      </p>
                      <p
                        style={{
                          marginBottom: "20px",
                          opacity: 0.4,
                          fontSize: "0.8em",
                        }}
                      >
                        Next charge -{" "}
                        {nextBillingDate
                          ? createFormattedDate(nextBillingDate)
                          : "N/A"}
                      </p>
                      {user.isPaused && (
                        <p
                          style={{
                            background: "#40CEE8",
                            borderRadius: "3px",
                            fontSize: "16px",
                            marginBottom: "14px",
                            padding: "10px",
                          }}
                        >
                          Please resume your subscription before making any
                          changes.
                        </p>
                      )}
                    </Fragment>
                  )}
                  <Button
                    primary
                    text="Change subscription"
                    extra={{
                      style: { margin: "20px" },
                      title: "Update your plan",
                    }}
                    disabled={
                      (selectedPlanId === user.subscription.planId &&
                        !isFamilyAddonsChanged &&
                        !isLocalProjectAddonsChanged) ||
                      user.isPaused
                    }
                    onClick={() => {
                      setUpdateModalVisibility(true);
                    }}
                  />
                </Fragment>
              )}
              <hr style={{ opacity: 0.4, margin: "30px 0 40px" }} />
              <Button
                small
                href="/account/manage-subscription"
                text="Back to subscription settings"
              />
            </Wrapper>
          </div>
        )}
      </Fragment>
      <PlanChangeConfirmModal
        close={() => {
          setUpdateModalVisibility(false);
        }}
        confirm={async () => {
          if (user && location && (selectedPlanId || isFamilyPlan)) {
            await updateSubscription({
              addons: selectedFamilyPlanAddons,
              shortCode: isFamilyPlan
                ? familyPlanIdForLocation
                : selectedPlanId!,
              localProjectAddon,
              numLocalTrees,
            });
            setUpdateModalVisibility(false);
          }
        }}
        isLoading={isLoading}
        isVisible={isUpdateModalVisible}
      />
    </Fragment>
  );
};

Plan.CheckoutForm = CheckoutFormWithWrapper;
